/**
 * Thread + message query definitions, cached under one key namespace shared
 * by both apps — previously "chat-threads"/"chat-messages" (apps/ai) and
 * "agent-chat-threads"/"agent-chat-messages" (apps/provider) held the same
 * server state under two different names.
 */
import { queryOptions } from "@tanstack/react-query";
import type { ChatAuth } from "./chat-fetch";
import { makeChatFetch } from "./chat-fetch";
import { type ChatMessage, type EngineMessage, toChatMessage } from "./message";
import { type ChatThread, type EngineThread, type ThreadId, toChatThread } from "./thread";

async function fetchJson<T>(auth: ChatAuth, baseUrl: string, path: string): Promise<T> {
  const chatFetch = makeChatFetch(auth, baseUrl);
  const res = await chatFetch(path);
  if (!res.ok) {
    throw new Error(`AI engine ${res.status} ${res.statusText} on ${path}`);
  }
  return (await res.json()) as T;
}

export const chatThreadQueries = {
  all: () => ["agent-chat-threads"] as const,

  list: (auth: ChatAuth, baseUrl: string) =>
    queryOptions({
      queryKey: chatThreadQueries.all(),
      queryFn: async (): Promise<ChatThread[]> => {
        const data = await fetchJson<EngineThread[]>(auth, baseUrl, "/chat/threads");
        return data.map(toChatThread);
      },
      staleTime: 1000 * 60,
    }),

  byId: (auth: ChatAuth, baseUrl: string, threadId: ThreadId) =>
    queryOptions({
      queryKey: [...chatThreadQueries.all(), threadId] as const,
      queryFn: async (): Promise<ChatThread> => {
        const data = await fetchJson<EngineThread>(auth, baseUrl, `/chat/threads/${threadId}`);
        return toChatThread(data);
      },
      staleTime: 1000 * 60,
    }),
};

export const chatMessageQueries = {
  all: () => ["agent-chat-messages"] as const,

  byThread: (auth: ChatAuth, baseUrl: string, threadId: ThreadId) =>
    queryOptions({
      queryKey: [...chatMessageQueries.all(), threadId] as const,
      queryFn: async (): Promise<ChatMessage[]> => {
        const data = await fetchJson<EngineMessage[]>(auth, baseUrl, `/chat/threads/${threadId}/messages`);
        return data.map(toChatMessage);
      },
      staleTime: 1000 * 10,
    }),
};
